import React from 'react';
import { Collapse, Card, Tag, Button, Tabs, Typography, Divider, Badge } from 'antd';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { solarizedlight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import RouteCopyButton from './RouteCopyButton';
import { ArrowRightOutlined } from '@ant-design/icons';
import { useTheme } from '../App';

const { Panel } = Collapse;
const { TabPane } = Tabs;
const { Paragraph } = Typography;
const SPECTER_ENV_URL = process.env.REACT_APP_SPECTER_ENV_URL;

const ExampleDrawerContent = ({ examples, selectedApi, setExamplesDrawerVisible, setDocsDrawerVisible }) => {
  // Use theme from context
  const { theme, currentTheme } = useTheme();

  if (!examples || examples.length === 0) {
    return <Typography.Text style={{ color: theme.textSecondary }}>No examples available.</Typography.Text>;
  }
  
  const getBadgeColor = (status) => {
    if (status >= 200 && status < 300) return theme.success;
    if (status >= 300 && status < 400) return theme.accent;
    if (status >= 400 && status < 500) return theme.error;
    if (status >= 500) return '#a8071a';
    return theme.primary;
  };

  return (
    <div className="example-drawer-content">
      {/* Card at the top */}
      {selectedApi && (
        <Card
          bordered={false}
          style={{ 
            marginBottom: '20px',
            backgroundColor: theme.surface,
            border: `1px solid ${theme.border}`,
          }}
          bodyStyle={{ padding: '10px' }}
        >
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <Typography.Title level={5} style={{ margin: 0, color: theme.text }}>
                {selectedApi.name}
              </Typography.Title>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Typography.Text type="secondary" style={{ fontSize: '14px', color: theme.textSecondary }}>
                  {SPECTER_ENV_URL + selectedApi.route}
                </Typography.Text>
                <RouteCopyButton route={SPECTER_ENV_URL + selectedApi.route} />
              </div>
            </div>

            <div>
              <Tag
                style={{ fontSize: '14px', padding: '4px 8px' }}
                color={
                  selectedApi.method === 'GET'
                    ? 'green'
                    : selectedApi.method === 'POST'
                    ? 'blue'
                    : selectedApi.method === 'PUT'
                    ? 'orange'
                    : 'red'
                }
              >
                {selectedApi.method}
              </Tag>
            </div>
          </div>
        </Card>
      )}

      {/* View API Reference button */}
      {selectedApi?.longDescription && (
        <div 
          style={{
            display: 'flex', 
            justifyContent: 'flex-end', 
            marginBottom: '16px'
          }}
        >
          <Button
            type="dashed"
            shape="round"
            icon={<ArrowRightOutlined />}
            onClick={() => {
              setExamplesDrawerVisible(false);
              setDocsDrawerVisible(true);
            }}
            style={{
              backgroundColor: 'transparent',
              borderColor: theme.primary,
              color: theme.primary,
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = `${theme.primary}20`;
              e.currentTarget.style.borderColor = theme.primary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.borderColor = theme.primary;
            }}
          >
            View API Reference
          </Button>
        </div>
      )}

      <Collapse 
        accordion
        style={{
          backgroundColor: theme.surface,
          border: `1px solid ${theme.border}`,
        }}
      >
        {examples.map((ex, index) => {
          const headerContent = (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              color: theme.text 
            }}>
              <span style={{ fontWeight: '500' }}>{ex.scenario}</span>
              {ex.response && ex.response.status && (
                <Badge 
                  count={ex.response.status} 
                  style={{ 
                    backgroundColor: getBadgeColor(ex.response.status), 
                    fontSize: '0.85em',
                    color: '#ffffff',
                    boxShadow: `0 0 0 1px ${theme.surface}`,
                  }}
                  showZero
                  overflowCount={1000} 
                />
              )}
            </div>
          );
          
          return (
            <Panel 
              header={headerContent} 
              key={index}
              style={{
                marginBottom: '8px',
                backgroundColor: theme.surfaceLight,
                borderColor: theme.border,
              }}
              className={currentTheme === 'dark' ? "dark-collapse-panel" : "light-collapse-panel"}
            >
              <Paragraph strong style={{ color: theme.text }}>Description:</Paragraph>
              <div
                className={`documentation-content ${currentTheme === 'dark' ? 'dark-theme' : ''}`}
                style={{
                  padding: '16px',
                  backgroundColor: theme.codeBg,
                  borderRadius: '6px',
                  fontSize: '14px',
                  lineHeight: '1.6',
                  color: theme.text,
                  marginBottom: '16px',
                  border: `1px solid ${theme.border}`,
                }}
                dangerouslySetInnerHTML={{ __html: ex.description }}
              />
              
              <Divider style={{ borderColor: theme.border, margin: '16px 0' }} />
              
              <Tabs 
                defaultActiveKey="request" 
                type="card"
                style={{ color: theme.text }}
              >
                <TabPane tab="Request" key="request">
                  <Tabs defaultActiveKey="headers" type="line">
                    <TabPane tab="Headers" key="headers">
                      <div style={{
                        backgroundColor: theme.codeBg,
                        borderRadius: '6px',
                        overflow: 'hidden',
                        border: `1px solid ${theme.border}`,
                      }}>
                        <SyntaxHighlighter 
                          language="json" 
                          style={currentTheme === 'dark' ? vscDarkPlus : solarizedlight}
                          customStyle={{
                            margin: 0,
                            backgroundColor: theme.codeBg,
                            fontSize: '13px',
                          }}
                        >
                          {ex.request.headers}
                        </SyntaxHighlighter>
                      </div>
                    </TabPane>
                    <TabPane tab="Body" key="body">
                      <div style={{
                        backgroundColor: theme.codeBg,
                        borderRadius: '6px',
                        overflow: 'hidden',
                        border: `1px solid ${theme.border}`,
                      }}>
                        <SyntaxHighlighter 
                          language="json" 
                          style={currentTheme === 'dark' ? vscDarkPlus : solarizedlight}
                          customStyle={{
                            margin: 0,
                            backgroundColor: theme.codeBg,
                            fontSize: '13px',
                          }}
                        >
                          {ex.request.body}
                        </SyntaxHighlighter>
                      </div>
                    </TabPane>
                  </Tabs>
                </TabPane>
                <TabPane tab="Response" key="response">
                  <Tabs defaultActiveKey="headers" type="line">
                    <TabPane tab="Headers" key="headers">
                      <div style={{
                        backgroundColor: theme.codeBg,
                        borderRadius: '6px',
                        overflow: 'hidden',
                        border: `1px solid ${theme.border}`,
                      }}>
                        <SyntaxHighlighter 
                          language="json" 
                          style={currentTheme === 'dark' ? vscDarkPlus : solarizedlight}
                          customStyle={{
                            margin: 0,
                            backgroundColor: theme.codeBg,
                            fontSize: '13px',
                          }}
                        >
                          {ex.response.headers}
                        </SyntaxHighlighter>
                      </div>
                    </TabPane>
                    <TabPane tab="Body" key="body">
                      <div style={{
                        backgroundColor: theme.codeBg,
                        borderRadius: '6px',
                        overflow: 'hidden',
                        border: `1px solid ${theme.border}`,
                      }}>
                        <SyntaxHighlighter 
                          language="json" 
                          style={currentTheme === 'dark' ? vscDarkPlus : solarizedlight}
                          customStyle={{
                            margin: 0,
                            backgroundColor: theme.codeBg,
                            fontSize: '13px',
                          }}
                        >
                          {ex.response.body}
                        </SyntaxHighlighter>
                      </div>
                    </TabPane>
                  </Tabs>
                </TabPane>
              </Tabs>
            </Panel>
          );
        })}
      </Collapse>
    </div>
  );
};

export default ExampleDrawerContent;